import React, { useState, useEffect } from 'react';
import { Briefcase, Play, Trash2, Plus, Sparkles, FolderOpen, Calendar, Key } from 'lucide-react';
import { CustomProject } from '../../types/productBuilder';
import { useAuth } from '../../lib/AuthContext';

interface MyProjectsProps {
  onLoadProject: (p: CustomProject) => void;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
  activeProjectId: string;
}

export const MyProjects: React.FC<MyProjectsProps> = ({ onLoadProject, onAddLog, activeProjectId }) => {
  const [projects, setProjects] = useState<CustomProject[]>([]);
  const { user } = useAuth();
  const ownerId = user?.uid || 'guest_owner_secure_07';

  // Load saved projects from server APIs
  const loadSavedList = async () => {
    if (!user) {
      // Unauthenticated / guest session local listing
      const savedGuest = localStorage.getItem('guest_temporary_project');
      if (savedGuest) {
        try {
          setProjects([JSON.parse(savedGuest)]);
        } catch (e) {
          console.error("Failed to parse cached guest project", e);
          setProjects([]);
        }
      } else {
        setProjects([]);
      }
      return;
    }

    try {
      const idToken = await user.getIdToken();
      const res = await fetch('/api/product-builder/projects', {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });
      const data = await res.json();
      if (data.success && data.projects) {
        setProjects(data.projects);
      } else {
        setProjects([]);
      }
    } catch (err) {
      console.error("Failed to load projects from server", err);
      setProjects([]);
    }
  };

  useEffect(() => {
    loadSavedList();
  }, [ownerId, user?.uid, user?.email]);

  const handleDelete = async (id: string, name: string) => {
    if (!user) {
      // Guest session local delete
      const doubleCheck = window.confirm(`Purge local guest blueprint "${name}" from your browser cache?`);
      if (!doubleCheck) return;
      localStorage.removeItem('guest_temporary_project');
      onAddLog('Product Builder', `Deleted guest project blueprint "${name}"`, 'WARNING', 'Purged local guest data from browser cache.');
      loadSavedList();
      return;
    }

    const doubleCheck = window.confirm(`Purge project blueprint "${name}" from cloud storage?`);
    if (!doubleCheck) return;

    try {
      const idToken = await user.getIdToken();
      const res = await fetch(`/api/product-builder/projects/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });
      const data = await res.json();
      if (data.success) {
        onAddLog('Product Builder', `Deleted project blueprint "${name}"`, 'WARNING', 'Purged custom files reference server-side.');
        loadSavedList();
      } else {
        alert(`Delete failed: ${data.error}`);
      }
    } catch (err: any) {
      alert(`Delete failed: ${err.message}`);
    }
  };

  return (
    <div className="npx-builder-my-projects-container space-y-6 text-left animate-fade-in">
      <div className="border-b border-white/5 pb-4 flex flex-wrap justify-between items-end gap-3 font-mono">
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <FolderOpen className="w-4 h-4 text-[#00ffff]" />
            <h2 className="text-sm font-mono tracking-widest text-[#00ffff] uppercase font-black">8. My Projects Ledger</h2>
          </div>
          <p className="text-xs text-zinc-400 font-sans normal-case">Access and restore saved layout configurations. Every blueprint carries a unique secure project and owner trace.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {projects.map((proj) => {
          const isActive = proj.id === activeProjectId;
          const projOwner = (proj as any).ownerId || ownerId;
          return (
            <div
              key={proj.id}
              className={`p-5 rounded-2xl bg-black/40 border transition-all text-left flex flex-col justify-between ${isActive ? 'border-[#00ffff]/40 bg-zinc-950 shadow-[0_0_20px_rgba(0,255,255,0.05)]' : 'border-white/5 bg-black/35 hover:border-zinc-800'}`}
            >
              <div className="space-y-3 font-mono">
                <div className="flex justify-between items-center text-[7.5px] font-black uppercase">
                  <span className="text-[#00ffff] bg-cyan-950/40 px-2 py-0.5 rounded border border-[#00ffff]/10">
                    {proj.industry}
                  </span>
                  <span className="text-zinc-500 flex items-center gap-1">
                    <Calendar size={10} />
                    <span>{new Date(proj.createdAt).toLocaleDateString()}</span>
                  </span>
                </div>

                <div className="space-y-0.5 text-left">
                  <h3 className="text-xs font-bold text-white uppercase tracking-wider">{proj.name}</h3>
                  <p className="text-[10px] text-zinc-500 font-sans leading-none">{proj.projectType}</p>
                </div>

                <div className="p-2 bg-black/50 rounded-lg text-[7.5px] text-zinc-500 space-y-1 leading-none border border-zinc-950">
                  <div className="flex justify-between">
                    <span>PROJECT ID:</span>
                    <span className="text-zinc-400 font-bold">{proj.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>OWNER ID:</span>
                    <span className="text-zinc-400 font-bold truncate max-w-[150px]">{projOwner}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  onClick={() => onLoadProject(proj)}
                  disabled={isActive}
                  className={`flex-grow font-mono text-[9px] font-black uppercase py-2.5 rounded-xl text-center cursor-pointer transition-colors duration-150 flex items-center justify-center gap-1 border ${isActive ? 'bg-zinc-900 border-zinc-800 text-zinc-500 cursor-not-allowed' : 'bg-gradient-to-r from-purple-900 to-indigo-950 hover:bg-gradient-to-r hover:from-purple-800 hover:to-indigo-950 border-purple-500/20 hover:border-purple-500/40 text-white'}`}
                >
                  <Play size={11} className="text-current" />
                  <span>{isActive ? 'Active Workspace' : 'Load Project'}</span>
                </button>

                <button
                  onClick={() => handleDelete(proj.id, proj.name)}
                  className="px-3 bg-red-950/20 hover:bg-red-950 border border-red-900/30 text-red-400 hover:text-white rounded-xl transition-all cursor-pointer flex items-center justify-center"
                  title="Purge Project"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
